import React, { useEffect, useState } from "react";
import { Card, Typography, Tag, Space, Input, Button, Tooltip } from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { BankOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as adminactions from "store/actions/AuthActions";
import useUrlFilters from "hooks/useUrlFilters";

const { Title, Text } = Typography;

const organization = process.env.REACT_APP_ORGANIZATION_UUID;

const URL_FILTERS = {
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const BankStatementsView = () => {
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [bankStatements, setBankStatements] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [searchText, setSearchText] = useState("");

    const fetchBankStatements = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const data = await adminactions.loadBankStatements(organization, page, pageSize);
            if (data) {
                setBankStatements(data.results || data);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: data.count || data.length,
                });
            }
        } catch (error) {
            console.error("Error fetching bank statements:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBankStatements(filters.page, pageSize);
    }, [filters.page, pageSize]);

    const getStatusTag = (status) => {
        const statusColors = {
            'PENDING': 'default',
            'PROCESSING': 'processing',
            'COMPLETED': 'success',
            'FAILED': 'error',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 120,
            render: (id) => (
                <Tooltip title={id}>
                    <Text copyable={{ text: id }}>{id.substring(0, 8)}...</Text>
                </Tooltip>
            ),
        },
        {
            title: "Bank Name",
            dataIndex: "bank_name",
            key: "bank_name",
            width: 150,
        },
        {
            title: "Account Number",
            dataIndex: "account_number",
            key: "account_number",
            width: 150,
        },
        {
            title: "Original Filename",
            dataIndex: "original_filename",
            key: "original_filename",
            width: 180,
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (status) => getStatusTag(status),
        },
        {
            title: "Txns",
            dataIndex: "transactions_count",
            key: "transactions_count",
            width: 70,
            align: "right",
        },
        {
            title: "Total Deposits",
            dataIndex: "total_deposits",
            key: "total_deposits",
            width: 130,
            align: "right",
            render: (value) => value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-',
        },
        {
            title: "Total Withdrawals",
            dataIndex: "total_withdrawals",
            key: "total_withdrawals",
            width: 140,
            align: "right",
            render: (value) => value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-',
        },
        {
            title: "Statement Period",
            key: "period",
            width: 200,
            render: (_, record) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {record.statement_start_date ? dayjs(record.statement_start_date).format('DD/MM/YYYY') : '-'}
                    {' - '}
                    {record.statement_end_date ? dayjs(record.statement_end_date).format('DD/MM/YYYY') : '-'}
                </span>
            ),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
        },
    ];

    const handleTableChange = (newPagination) => {
        const sizeChanged = newPagination.pageSize !== pageSize;
        setFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
    };

    return (
        <div style={{ padding: "24px" }}>
            <Card
                title={
                    <Space>
                        <BankOutlined />
                        <Title level={4} style={{ margin: 0 }}>Bank Statements (View Only)</Title>
                    </Space>
                }
                extra={
                    <Space>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={() => fetchBankStatements(1, pagination.pageSize)}
                            style={{ width: 200 }}
                        />
                        <Tooltip title="Refresh">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchBankStatements(filters.page, pageSize)}
                                aria-label="Refresh"
                            />
                        </Tooltip>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={bankStatements}
                    rowKey="id"
                    loading={loading}
                    pagination={{ ...pagination, showSizeChanger: true }}
                    onChange={handleTableChange}
                    scroll={{ x: 1400 }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default BankStatementsView;
